import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useCountryMarketState } from '../../controllers/CountryMarketController/CountryMarketState';
import CountryMarketSelector from '../../features/CountryMarketSelector/CountryMarketSelector';
import { useAppRoutes } from '../../hooks/useAppRoutes';
import { useAuth } from '../../hooks/useAuth';
import { Icon, IconName } from '../Icon';

type MenuItem = {
  to: string;
  title: string;
  icon: IconName;
};

export const SiteHeader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    briefingSelectionRoute,
    projectOverviewRoute,
    introRoute,
    accountRoute,
  } = useAppRoutes();

  const [showMenu, setShowMenu] = useState(false);
  const [showCountryMarketSelector, setShowCountryMarketSelector] =
    useState(false);
  const [inBriefing, setInBriefing] = useState(false);

  const toggleMenu = useCallback(() => {
    setShowMenu(!showMenu);
  }, [showMenu]);

  const HEADER_BASE_CLASSES = `flex sticky lg:flex-col flex-shrink-0 flex-row justify-between flex-wrap md:flex-nowrap bg-gray-700 text-white md:px-6 lg:px-0 z-50 top-0 left-0`;
  const HEADER_DEFAULT_CLASSES = `${HEADER_BASE_CLASSES} lg:h-screen lg:w-auto`;
  const HEADER_BRIEFING_CLASSES = `${HEADER_BASE_CLASSES} border-solid border-b-[12px] lg:border-r-[12px] border-gray-700 h-0 lg:h-auto lg:w-0 overflow-hidden`;

  const LINK_CLASSES =
    'py-2 md:py-0 lg:py-4 h-16 md:h-auto lg:h-6 hover:bg-blue-100 flex md:justify-center md:flex-col md:px-4 lg:px-0 md:w-6 lg:w-full md:box-content cursor-pointer items-center md:items-stretch';

  const [{ countryMarketConfig }] = useCountryMarketState();

  useEffect(() => {
    setInBriefing(location.pathname.includes('/briefing'));
    setShowMenu(false);
  }, [location.pathname]);

  const [{ auth, user, customers }, { signOut }] = useAuth();

  const menuItems = useMemo(
    () =>
      (
        [
          {
            to: briefingSelectionRoute,
            title: 'start',
            icon: 'FileAdd',
          },
          {
            to: projectOverviewRoute,
            title: 'project-overview',
            icon: 'Files',
          },
        ] as MenuItem[]
      ).map((item, index) => (
        <Link
          key={index}
          to={item.to}
          title={t(`linksOrButtons:navigation:${item.title}`)}
          className={LINK_CLASSES}
        >
          <span className="flex-shrink-0 w-6 ml-4 text-white md:w-auto md:ml-0 lg:w-6 lg:mx-auto">
            <Icon name={item.icon} />
          </span>
          <span className="ml-3 md:hidden md:ml-0 font-regular">
            {t(`linksOrButtons:navigation:${item.title}`)}
          </span>
        </Link>
      )),
    [t, briefingSelectionRoute, projectOverviewRoute],
  );

  const hasUserSelectionPossibilitiesInPreferences = useMemo(
    () =>
      Object.keys(user?.countries_and_markets || {}).length > 1 ||
      Object.keys(customers || {}).length > 1 ||
      (!!countryMarketConfig && countryMarketConfig.languages?.length > 1),
    [countryMarketConfig, customers, user],
  );

  const isPreferencesVisible = useMemo(
    () => !user?.azelis_employee && hasUserSelectionPossibilitiesInPreferences,
    [hasUserSelectionPossibilitiesInPreferences, user],
  );

  return (
    <>
      <header
        className={
          inBriefing ? HEADER_BRIEFING_CLASSES : HEADER_DEFAULT_CLASSES
        }
      >
        <h1 className="flex-shrink-0 order-none">
          <Link to={introRoute}>
            <img
              src="/logo.svg"
              alt={t('labels:eLab')}
              className="h-full lg:m-auto lg:h-auto py-3 ml-6 md:ml-4 lg:py-6 lg:mx-auto lg:px-2"
            />
          </Link>
        </h1>
        {auth.isLoaded && (
          <div
            className={`${
              showMenu ? '' : 'hidden'
            } md:flex lg:flex-col lg:flex-grow md:justify-end lg:justify-between order-3 md:order-none w-full bg-gray-700 pt-2 md:pt-0 h-screen md:h-auto`}
          >
            <nav className="flex flex-col md:flex-row lg:flex-col md:items-stretch">
              {!auth.isEmpty && menuItems}
            </nav>
            <nav className="flex flex-col md:flex-row lg:flex-col md:items-stretch">
              {auth.isEmpty ? (
                <Link
                  to={introRoute}
                  title={t('labels:signIn')}
                  className={LINK_CLASSES}
                >
                  <span className="w-8 ml-3 text-white md:w-auto md:ml-0 lg:w-6 lg:mx-auto" data-testid="intro">
                    <Icon name={'SignIn'} />
                  </span>
                  <span className="ml-3 md:hidden md:ml-0 font-regular">
                    {t('labels:signIn')}
                  </span>
                </Link>
              ) : (
                <>
                  {isPreferencesVisible && (
                    <span
                      onClick={() => {
                        setShowMenu(false);
                        setShowCountryMarketSelector(true);
                      }}
                      title={t(
                        'components:countryMarketSelector:reason:preferences:heading',
                      )}
                      className={LINK_CLASSES}
                    >
                      <span className="w-6 ml-4 text-white md:w-auto md:ml-0 lg:w-6 lg:mx-auto">
                        <Icon name={'Location'} />
                      </span>
                      <span className="ml-3 md:hidden md:ml-0 font-regular">
                        {t(
                          'components:countryMarketSelector:reason:preferences:heading',
                        )}
                      </span>
                    </span>
                  )}
                  <Link
                    to={accountRoute}
                    title={t('components:account:heading')}
                    className={LINK_CLASSES}
                  >
                    <span className="w-6 ml-4 text-white md:w-auto md:ml-0 lg:w-6 lg:mx-auto">
                      <Icon name={'Account'} />
                    </span>
                    <span className="ml-3 md:hidden md:ml-0 font-regular">
                      {t('components:account:heading')}
                    </span>
                  </Link>
                  <Link
                    to={introRoute}
                    className={LINK_CLASSES}
                    onClick={() => {
                      signOut();
                    }}
                  >
                    <span className="w-6 ml-4 text-white md:w-auto md:ml-0 lg:w-6 lg:mx-auto" data-testid="sign-out">
                      <Icon name={'SignOut'} />
                    </span>
                    <span className="ml-3 md:hidden md:ml-0 font-regular">
                      {t('labels:signOut')}
                    </span>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
        <button className="order-none md:hidden" onClick={toggleMenu}>
          <span className="block w-6 m-4">
            <Icon name={'Menu'} />
          </span>
        </button>
      </header>

      <CountryMarketSelector
        reason="preferences"
        isHidden={!showCountryMarketSelector}
        onCancel={() => {
          setShowCountryMarketSelector(false);
        }}
        onConfirm={() => {
          setShowCountryMarketSelector(false);
        }}
      />
    </>
  );
};
